import { useState } from "react";
import { API } from "@/lib/api";
import { Box, Expand } from "lucide-react";
import Lightbox from "@/components/Lightbox";

/**
 * AngleGallery
 * ------------
 * Static product image gallery for a listing.
 *
 *   props.listing – enriched listing doc from `/api/listings/{id}`
 *   props.fallbackNode – optional node to render when no angle previews exist
 *     (typically the interactive CadViewer, so older listings still work)
 *
 * Clicking the main image (or any thumbnail) opens a fullscreen `Lightbox`
 * with zoom + pan + keyboard navigation so buyers can inspect fine detail
 * before deciding to load the heavy interactive 3D preview.
 */
export default function AngleGallery({ listing, fallbackNode }) {
  const angles = listing?.angle_previews || [];
  const urls = angles.map((p) => (p.startsWith("http") ? p : `${API.replace(/\/api$/, "")}${p}`));
  const [active, setActive] = useState(0);
  const [lightboxAt, setLightboxAt] = useState(-1); // -1 = closed

  if (urls.length === 0) {
    // Older listings — fall back to the interactive viewer (or a plain box)
    return (
      fallbackNode || (
        <div className="w-full h-full flex items-center justify-center bg-slate-900/40 rounded-md">
          <Box className="h-12 w-12 text-slate-500" />
        </div>
      )
    );
  }

  return (
    <div className="w-full h-full flex flex-col gap-3" data-testid="angle-gallery">
      <button
        type="button"
        onClick={() => setLightboxAt(active)}
        data-testid="angle-gallery-main-btn"
        className="flex-1 relative rounded-md overflow-hidden border border-border group cursor-zoom-in"
        style={{
          background:
            "radial-gradient(circle at 50% 40%, #ffffff 0%, #f4f6fa 60%, #e6ebf3 100%)",
        }}
        aria-label="Open image in fullscreen"
      >
        <img
          src={`${urls[active]}${urls[active].includes("?") ? "&" : "?"}w=900`}
          alt={`${listing.title} — angle ${active + 1}`}
          className="w-full h-full object-contain p-4"
          loading="eager"
          decoding="async"
          data-testid="angle-gallery-main"
        />
        <span className="absolute bottom-2 left-2 font-mono text-[10px] uppercase tracking-widest bg-black/70 text-white px-2 py-0.5 rounded">
          {active + 1} / {urls.length}
        </span>
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest bg-black/60 text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
          <Expand className="h-3 w-3" /> Zoom
        </span>
      </button>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {urls.map((u, i) => (
          <button
            key={u}
            onClick={() => setActive(i)}
            onDoubleClick={() => setLightboxAt(i)}
            data-testid={`angle-thumb-${i}`}
            className={`h-16 w-16 shrink-0 rounded overflow-hidden border-2 transition-colors ${
              active === i ? "border-primary" : "border-transparent hover:border-primary/50"
            }`}
            style={{ background: "#f4f6fa" }}
            aria-label={`Show angle ${i + 1}`}
          >
            <img src={`${u}${u.includes("?") ? "&" : "?"}w=160`} alt="" className="w-full h-full object-contain" loading="lazy" decoding="async" />
          </button>
        ))}
      </div>

      {lightboxAt >= 0 && (
        <Lightbox
          images={urls}
          index={lightboxAt}
          onClose={() => setLightboxAt(-1)}
          onIndex={(i) => { setLightboxAt(i); setActive(i); }}
        />
      )}
    </div>
  );
}
